import type { CustomerCreateRequest, LegalRepresentative } from '../api/customers'
import { cfNamesCheck, splitNameByCf } from './codiceFiscale'

/** Codice fiscale o P.IVA letti da un documento, normalizzati per cercare il cliente prima di crearne uno nuovo. */
export function docIdentity(values: Record<string, string | undefined>): string {
  for (const key of ['partitaIva', 'codiceFiscale']) {
    const v = String(values[key] ?? '').replace(/[\s.\-]/g, '').toUpperCase().replace(/^IT(?=\d{11}$)/, '')
    if (/^\d{11}$/.test(v) || /^[A-Z0-9]{16}$/.test(v)) return v
  }
  return ''
}

/**
 * Anagrafica da creare al salvataggio della pratica quando nessun cliente esiste: dati del portale (già precompilati
 * dai documenti e corretti dall'operatore) più quelli letti dai documenti. Visura = società, altrimenti persona.
 * Null se manca il nome (o la ragione sociale): meglio nessun cliente che uno vuoto.
 */
export function customerFromDocs(
  portal: Record<string, string | undefined>,
  docs: { carta_identita?: Record<string, string>; visura?: Record<string, string> },
): CustomerCreateRequest | null {
  const p = (k: string) => String(portal[k] ?? '').trim() || undefined
  const visura = docs.visura ?? {}
  const ci = docs.carta_identita ?? {}
  const street = [p('INDIRIZZO_CLIENTE'), p('NUM_CIVICO_CLIENTE')].filter(Boolean).join(' ') || undefined
  const common = {
    email: p('EMAIL_SPED'), phone: p('TEL_CLIENTE'), address: street, city: p('ID_COMUNE_CLIENTE'),
    province: p('PROVINCIA_CLIENTE'), postalCode: p('CAP_CLIENTE'), country: 'IT',
  }
  const company = p('RAGIONE_SOCIALE_CLIENTE') ?? (visura['ragioneSociale']?.trim() || undefined)
  if (company) {
    return {
      ...common, customerType: 'company', companyName: company,
      vatNumber: p('PARTITA_IVA_CLIENTE') ?? (visura['partitaIva']?.trim() || undefined),
      fiscalCode: p('CODICE_FISCALE_CLIENTE') ?? (visura['codiceFiscale']?.trim() || undefined),
      pec: p('EMAIL_PEC_CLIENTE') ?? (visura['pec']?.trim() || undefined),
    }
  }
  const firstName = p('NOME_CLIENTE') ?? (ci['nome']?.trim() || undefined)
  const lastName = p('COGNOME_CLIENTE') ?? (ci['cognome']?.trim() || undefined)
  if (!firstName && !lastName) return null
  const birth = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(ci['dataNascita']?.trim() ?? '')
  return {
    ...common, customerType: 'person', firstName, lastName,
    fiscalCode: p('CODICE_FISCALE_CLIENTE') ?? (ci['codiceFiscale']?.trim() || undefined),
    birthDate: birth ? `${birth[3]}-${birth[2]!.padStart(2, '0')}-${birth[1]!.padStart(2, '0')}` : undefined,
    birthCity: ci['luogoNascita']?.trim() || undefined,
  }
}

/**
 * Rappresentante legale di un cliente azienda dai documenti della pratica: chi firma presenta la propria carta
 * d'identità. Senza carta si usa il nome scritto nei dati portale, diviso in nome e cognome secondo il codice fiscale
 * se c'è (la visura scrive «Cognome Nome»), altrimenti prima parola = nome. Null se manca il nome.
 */
export function legalRepFromDocs(
  portal: Record<string, string | undefined>,
  ci: Record<string, string | undefined>,
): LegalRepresentative | null {
  const t = (v: string | undefined) => v?.trim() || undefined
  const cf = t(ci['codiceFiscale'])
  let firstName = t(ci['nome'])
  let lastName = t(ci['cognome'])
  if (!firstName && !lastName) {
    const { nome, cognome } = splitNameByCf(t(portal['LEGALE_RAPPRESENTANTE_CLIENTE']), cf)
    firstName = nome || undefined
    lastName = cognome || undefined
  } else if (cfNamesCheck(cf, firstName, lastName) === 'swapped') {
    // letti al contrario dalla carta: il codice fiscale dice qual è il cognome
    ;[firstName, lastName] = [lastName, firstName]
  }
  if (!firstName && !lastName) return null
  const birth = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(t(ci['dataNascita']) ?? '')
  return {
    firstName: firstName ?? null, lastName: lastName ?? null,
    fiscalCode: cf ?? null,
    birthDate: birth ? `${birth[3]}-${birth[2]!.padStart(2, '0')}-${birth[1]!.padStart(2, '0')}` : null,
    birthCity: t(ci['luogoNascita']) ?? null,
  }
}
